import { useState, useCallback, useEffect, useRef } from 'react';
import EZIOKService, {
  EZIOKRequestParams,
  EZIOKResult,
  EZIOKProvider,
} from '../libs/EZIOKService';
import axiosClient from '../libs/axiosClient';

// 글로벌 콜백 함수를 위한 Window 확장
declare global {
  interface Window {
    eziokCallback?: (response: string) => void;
  }
}

interface UseEZIOKOptions {
  // 인증 성공 콜백
  onSuccess?: (result: EZIOKResult) => void;
  // 인증 실패 콜백
  onError?: (error: Error) => void;
  // 인증 취소 콜백
  onCancel?: () => void;
}

interface UseEZIOKReturn {
  // 간편인증 시작
  startVerification: (params: EZIOKRequestParams) => Promise<void>;
  // 인증사업자 목록 조회
  getProviders: () => Promise<EZIOKProvider[]>;
  // 로딩 상태
  isLoading: boolean;
  // 에러 상태
  error: Error | null;
  // SDK 로드 상태
  isSDKLoaded: boolean;
  // 인증사업자 목록
  providers: EZIOKProvider[];
}

/**
 * EZ-IOK 간편인증 커스텀 훅
 *
 * @example
 * const { startVerification, getProviders, isLoading, providers } = useEZIOK({
 *   onSuccess: (result) => console.log('인증 성공:', result),
 *   onError: (error) => console.error('인증 실패:', error),
 * });
 *
 * // 인증사업자 목록 조회
 * await getProviders();
 *
 * // 간편인증 시작 (통합창)
 * await startVerification({
 *   resultUrl: `https://api.slimrx.co.kr/func/eziok/callback`,
 *   authProviderCode: 'kakao', // 필수
 * });
 */
export function useEZIOK(options?: UseEZIOKOptions): UseEZIOKReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const [providers, setProviders] = useState<EZIOKProvider[]>([]);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // SDK 사전 로드 및 글로벌 콜백 함수 등록
  useEffect(() => {
    EZIOKService.loadScript()
      .then(() => setIsSDKLoaded(true))
      .catch((err) => {
        console.error('[useEZIOK] SDK load failed:', err);
        setIsSDKLoaded(false);
      });

    // 글로벌 콜백 함수 등록
    // EZ-IOK는 "결과코드|결과메시지" 형식의 파이프 구분 문자열로 반환
    window.eziokCallback = (response: string) => {
      console.log('[EZIOK] Callback received:', response);
      try {
        // 파이프(|) 구분 문자열 파싱
        const parts = response.split('|');
        const resultCode = parts[0];
        const resultMsg = parts[1] || '';

        console.log('[EZIOK] Parsed callback:', { resultCode, resultMsg });

        if (resultCode === '0') {
          // 성공 - resultMsg에 JSON이 포함되어 있을 수 있음
          try {
            const data = JSON.parse(resultMsg);
            optionsRef.current?.onSuccess?.(data);
          } catch {
            // JSON이 아니면 기본 성공 응답으로 처리
            optionsRef.current?.onSuccess?.({
              success: true,
              message: resultMsg,
              data: {},
            });
          }
        } else {
          const error = new Error(resultMsg || '간편인증에 실패했습니다.');
          optionsRef.current?.onError?.(error);
        }
      } catch (err) {
        console.error('[EZIOK] Callback parse error:', err);
        optionsRef.current?.onError?.(err instanceof Error ? err : new Error('콜백 처리 실패'));
      }
    };

    return () => {
      // 컴포넌트 언마운트 시 콜백 제거
      delete window.eziokCallback;
    };
  }, []);

  // 인증사업자 목록 조회
  const getProviders = useCallback(async (): Promise<EZIOKProvider[]> => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.slimrx.co.kr';
      const response = await axiosClient.get<{ providers: EZIOKProvider[] }>(
        `${API_BASE}/func/eziok/providers`,
      );
      const providerList = response.data.providers || [];
      setProviders(providerList);
      return providerList;
    } catch (err) {
      console.error('[useEZIOK] Failed to get providers:', err);
      return [];
    }
  }, []);

  // 간편인증 시작
  const startVerification = useCallback(
    async (params: EZIOKRequestParams) => {
      console.log('[EZIOK] startVerification called with:', params);
      setIsLoading(true);
      setError(null);

      try {
        // 인증사업자 코드 필수 체크
        if (!params.authProviderCode) {
          throw new Error('인증사업자 코드(authProviderCode)가 필요합니다.');
        }

        // 새 API: EZIOKService.startAuth 사용
        await EZIOKService.startAuth(params);

        // 팝업이 열리면 로딩 상태 해제
        setIsLoading(false);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('간편인증 요청 실패');
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
        throw error;
      }
    },
    [options],
  );

  return {
    startVerification,
    getProviders,
    isLoading,
    error,
    isSDKLoaded,
    providers,
  };
}

/**
 * 간편인증 결과 처리 훅 (콜백 페이지용)
 *
 * @example
 * // /auth/eziok/callback 페이지에서 사용
 * const { result, isLoading, error } = useEZIOKCallback({
 *   onSuccess: (result) => navigate('/join/complete'),
 *   onError: (error) => navigate('/join?error=verification'),
 * });
 */
export function useEZIOKCallback(options?: UseEZIOKOptions) {
  const [result, setResult] = useState<EZIOKResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // URL에서 파라미터 추출
        const urlParams = new URLSearchParams(window.location.search);
        // 백엔드에서 'token'으로 전달하므로 'token'과 'keyToken' 둘 다 확인
        const keyToken = urlParams.get('token') || urlParams.get('keyToken');
        const resultCode = urlParams.get('resultCode');

        // 에러 체크
        if (resultCode && resultCode !== '0') {
          const errorMsg =
            urlParams.get('resultMsg') || '간편인증에 실패했습니다.';
          throw new Error(errorMsg);
        }

        if (!keyToken) {
          throw new Error('인증 토큰이 없습니다.');
        }

        // 세션에서 거래 ID 확인 (CSRF 방지)
        const storedTxId = sessionStorage.getItem('eziok_txId');
        if (!storedTxId) {
          throw new Error('유효하지 않은 인증 요청입니다.');
        }

        // 백엔드에서 결과 조회 및 복호화
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.slimrx.co.kr';
        const response = await axiosClient.post<EZIOKResult>(
          `${API_BASE}/func/eziok/result`,
          {
            keyToken,
            txId: storedTxId,
          },
        );

        // 세션 정리
        sessionStorage.removeItem('eziok_txId');

        setResult(response.data);
        setIsLoading(false);
        options?.onSuccess?.(response.data);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('간편인증 결과 처리 실패');
        setError(error);
        setIsLoading(false);
        options?.onError?.(error);
      }
    };

    processCallback();
  }, []);

  return { result, isLoading, error };
}

export default useEZIOK;